import { ApiGroup } from './model/ApiGroup';
import { Integration } from './partner/partnerApiTypes';

export type User = {
  id: number;
  username: string;
  name: string;
  roles: string[];
  partnerCode: string;
  partnerName: string;
};

export type ChangePasswordRequest = {
  username: string;
  password: string;
  newPassword: string;
};

export type BackendExceptionResponse = {
  httpStatus: number;
  code: string;
  codeDescription: string;
  details: string;
};

export type PricingTriggersCreationResponse = {
  submittedCount: number;
};

export type OrderForLabelPrint = {
  id: number;
  originalId: string;
  source: string;
  shippingLabelsPrintMethod: LabelType;
  items: Item[];
  parcelShopBranchId?: string;
  deliveryManager: DeliveryManager;
  apiGroup: ApiGroup;
};

export type LabelType = 'SIMPLE' | 'DETAILED' | undefined;

export type SimpleOrder = {
  id: number;
  partnerId: string | null;
  originalId: string;
  source: string;
  hasLabels: boolean;
};
export type Item = {
  id: string;
  originalId: string;
  count: number;
};

export type DetailedLabelRequest = {
  numberOfPackages: number;
  packages: Package[];
};

export type Package = {
  packageId: number;
  weight?: number;
  dimension1?: number;
  dimension2?: number;
  dimension3?: number;
  volume?: number;
  size?: string;
  items: PackageItem[];
};

export type PackageItem = {
  id: string;
  count: number;
};

export type Product = {
  productId: string;
  globalOnHand: number;
  salesChannels: Array<string>;
  errorMessage: string
};

export type Category = {
  externalId: string;
};

export type CategoriesResponse = {
  categories: Category[]
};

export type Brand = {
  externalId: string;
  title: string;
};

export type BrandResponse = {
  brands: Brand[]
};

export type ApiGroupEshops = {
  id: number;
  code: string;
  name: string;
  baseMarketplaceCode?: string;
};

export type PartnerApiGroup = {
  code: ApiGroup;
  eshops: ApiGroupEshops[];
};

export type Partner = {
  id: number;
  name: string;
  code: string;
  apiGroups: PartnerApiGroup[]
};

export enum BaselinkerPriceSyncTarget {
  SALES_PRICE = 'SALES_PRICE',
  B2B_PRICE = 'B2B_PRICE',
}

export type IntegrationInfo = {
  baselinkerToken?: string;
  baselinkerPriceSyncTarget?: BaselinkerPriceSyncTarget;
  baselinkerB2bPriceCurrency?: string;
  baselinkerB2bPriceVat?: number;
};

export type PartnerEshopResponse = {
  partners: Partner[];
};

export interface PagedResponse<T> {
  total: number,
  page: number,
  pageSize: number,
  data: T[]
}

export type SortDirection = 'asc' | 'desc';

export interface ListQuery<T> {
  page: number,
  orderBy: string,
  orderDirection: SortDirection,
  filter: T
}

export interface PagedRequest<T> extends ListQuery<T> {
  pageSize: number
}

export enum OrderStates {
  RETURNED = 'RETURNED',
  CANCELLED = 'CANCELLED',
  DELIVERED = 'DELIVERED',
  SHIPPED = 'SHIPPED',
  SHIPPING = 'SHIPPING',
  OPEN = 'OPEN',
  RESERVATION = 'RESERVATION',
}

export enum UpdateStatus {
  UPDATE_PENDING = 'UPDATE_PENDING',
  UP_TO_DATE = 'UP_TO_DATE',
}

export enum DeliveryManager {
  MARKETPLACE = 'MARKETPLACE',
  PARTNER = 'PARTNER',
  MPI = 'MPI',
}

export type OrdersCount = {
  count: number;
};

export type OrderListItem = {
  id: number;
  apiGroup: ApiGroup;
  marketplace: string;
  partnerId?: string;
  orderNumber: string;
  orderValue: number;
  orderValueCurrency: string;
  confirmed: string;
  status: OrderStates;
  statusDate: string;
  carrier: string;
  deliveryManager: DeliveryManager;
  suggestedExpedition: string;
  codValue: number;
  codValueCurrency: string;
  packagesCount: number;
  trackingNumber: string;
  shippingLabelUrl: string;
  shippingListUrl: string;
  updateStatus: UpdateStatus;
  updateStatusResponseError: string | null;
  allowedStates: OrderStates[];
  labelsError?: string;
  numberOfCrossDockTrackings?: number;
  crossDockDelivery: boolean;
  eshopCarrierType?: string;
  integration: Integration;
  precanceled: boolean;
  orderCountry?: string;
};

export type OrderUpdateStatus = {
  updateStatus: UpdateStatus;
  updateStatusResponseError: string | null;
};

export type RevertedOrderStatus = {
  orderId: number;
  status: OrderStates;
  allowedStatusChanges: OrderStates[]
};

export type OrderForPrintListItem = {
  id: number;
  marketplace: string;
  apiGroup: string;
  orderNumber: string;
  partnerId: string;
  carrier: string;
  deliveryManager: DeliveryManager;
  suggestedExpedition: string;
  pagesCount: number;
  crossDockDelivery: boolean;
};

export type ApiDateRange = {
  from: string;
  to: string;
};

export type NumberRange = {
  from: number | null;
  to: number | null;
};

export type CarrierFilter = {
  deliveryManager: DeliveryManager;
  apiGroup: ApiGroup;
  carrier: string
};

export type OrderListFilter = {
  globalFilter?: string;
  states: OrderStates[];
  eshops?: string[];
  carriers: CarrierFilter[];
  originalOrderId?: string;
  partnerOrderId?: string;
  orderValue?: NumberRange;
  expedition?: ApiDateRange;
  confirmed?: ApiDateRange;
  delivered?: ApiDateRange;
  shipping?: ApiDateRange;
  returned?: ApiDateRange;
  shipped?: ApiDateRange;
  cancelled?: ApiDateRange;
  crossDockDelivery?: boolean;
  onlyErrors?: boolean;
  precanceled?: boolean;
  alzaLinehaul?: boolean;
  alzaRegularDelivery?: boolean;
  orderCountry?: string;
};

export type DeliveryTypes = 'HOME_DELIVERY' | 'ALZA_PICKUP' | 'CUSTOMER_PICKUP_SHOP' | 'CUSTOMER_PICKUP_BOX';

export type MergeFormat = 'A4' | 'CONTINUOUS' | null;

export type PrintExistingOrdersFilter = {
  globalFilter?: string;
  deliveryTypes: DeliveryTypes[]
};

export type LabelsMergeRequest = {
  format: MergeFormat;
  orderIds: number[];
};

export type OrderStateUpdateEntity = {
  id: number;
  newState: OrderStates;
  trackingNumber: string;
  trackingUrl: string;
  cancelReason: string;
  marketplaceCarrierId?: string;
};

export type OrderPreviewProduct = {
  orderProductCode: string,
  ean: string,
  title: string,
  quantity: number,
  currency: string,
  vat: number,
  sellerUnitPrice?: number | null,
  customerUnitPrice?: number | null,
  invoiceUnitPrice?: number | null,
  payoutUnitPrice?: number | null,
};

export type OrderPreviewCustomer = {
  name: string,
  company?: string,
  phone?: string,
  email?: string,
  address1?: string,
  address2?: string,
  city?: string,
  zip?: string,
};

export type OrderPreview = {
  products: OrderPreviewProduct [],
  customer: OrderPreviewCustomer,

  parcelTracking: {
    customer: string [];
    crossDock: string[]
  }
};

export type ApiCarrier = {
  apiGroup: ApiGroup;
  identifier: string;
  deliveryManager: DeliveryManager;
};

export type PackageSizes = {
  size: string;
  weight: number;
  length: number;
  width: number;
  height: number;
};

export enum ProductStates {
  TO_BE_PUBLISHED = 'TO_BE_PUBLISHED',
  DRAFT = 'DRAFT',
  PUBLISHED_TEST = 'PUBLISHED_TEST',
  TO_BE_ACTIVATED = 'TO_BE_ACTIVATED',
  PUBLISHED_LIVE = 'PUBLISHED_LIVE',
  IMPORT_FAILED = 'IMPORT_FAILED',
  UPDATE_PENDING = 'UPDATE_PENDING',
  UPDATE_FAILED = 'UPDATE_FAILED',
  TO_BE_DELETED = 'TO_BE_DELETED',
  DELETION_FAILED = 'DELETION_FAILED',
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  MPI_ONLY = 'MPI_ONLY',
  ACTIVATION_FAILED = 'ACTIVATION_FAILED',
}

export enum PriceDecreasePossibility {
  POSSIBLE = 'POSSIBLE',
  NOT_POSSIBLE = 'NOT_POSSIBLE',
  NOT_NECESSARY = 'NOT_NECESSARY',
}

export enum PriceMode {
  MP_LOWEST_FROM_PA_B2B_PRICE = 'MP_LOWEST_FROM_PA_B2B_PRICE',
}

export enum PricingRequestStatus {
  PENDING = 'PENDING',
  DATA_FETCHED = 'DATA_FETCHED',
  PRICING_CALCULATED = 'PRICING_CALCULATED',
  PRICING_CALCULATED_ERROR = 'PRICING_CALCULATED_ERROR',
  ALERTS_PROCESSED = 'ALERTS_PROCESSED',
  ERROR = 'ERROR',
  REPRICED = 'REPRICED',
  REPRICING_FAILED = 'REPRICING_FAILED',
}

export enum HoldStatus {
  ON_HOLD = 'ON_HOLD',
  NOT_ON_HOLD = 'NOT_ON_HOLD',
}

export enum ProductTypes {
  PRODUCT = 'PRODUCT',
  VARIANT = 'VARIANT',
}

export enum PricingAlertPriority {
  CRITICAL = 'CRITICAL',
  MAJOR = 'MAJOR',
  MINOR = 'MINOR',
}

export enum PricingAlertAction {
  HOLD = 'HOLD',
}

export type PricingDataListItem = {
  ean?: string;
  sku?: string;
  mpId?: string;
  marketplaceCode?: string;
  offerStatus?: ProductStates;
  productName?: string;
  partnerBrand?: string;
  partnerSupplier?: string;
  partnerCustomField?: string;
  categoryId?: string;
  categoryName?: string;
  handlingTime?: number;
  onHold?: boolean;
  stock?: number;
  vat?: number;
  mpCurrency?: string;
  exchangeRate?: number;
  partnerB2cPrice?: number;
  recommendedRetailPrice?: number;
  minimumRecommendedRetailPrice?: number;
  forcedPrice?: number;
  currentCustomerPrice?: number;
  calculatedCustomerPrice?: number;
  calculatedMinimumCustomerPrice?: number;
  calculatedRegularCustomerPrice?: number;
  partnerMinRelativeMargin?: number;
  partnerMinAbsoluteMargin?: number;
  partnerRegularRelativeMargin?: number;
  partnerRegularAbsoluteMargin?: number;
  basePriceInMarketplaceCurrency?: number;
  partnerDeliveryCostWoVat?: number;
  partnerWarehouseCostWoVat?: number;
  partnerReturnCostWoVat?: number;
  partnerAdditionalFixFee?: number;
  scCategoryRelativeCommission?: number;
  mpRelativeCommissionWithVat?: number;
  mpRelativeCommissionWithoutVat?: number;
  mpAdditionalFixFee?: number;
  calcMinimumBasisPrice?: number;
  calcRegularBasisPrice?: number;
  calcPayoutPrice?: number;
  mpLowestPrice?: number;
  marketLowestPrice?: number;
  competitionCheaperOnMarketplace?: boolean;
  marketCompetitionCheaper?: boolean;
  priceDecreasePossibility?: PriceDecreasePossibility;
  priceSpread?: number;
  priceMode?: PriceMode;
  decreaseByAbs?: number;
  pricingNote?: string;
  pricingRequestStatus?: PricingRequestStatus;
  pricingRequestMessage?: string;
  pricingRequestCreatedAt?: string;
  errorMessage?: string
  originalB2bPrice?: number;
  originalB2bPriceVat?: number;
  partnerOriginalB2bPriceCurrency?: string;
  alertsByPriority?: Partial<Record<PricingAlertPriority, string[]>>;
  afterAlertingPrice?: number;
  alertAction?: PricingAlertAction;
};

export type PricingDataListFilter = {
  marketplaceCodes: string[];
  priceModes?: PriceMode[];
  vat?: number;
  originalB2bPriceRange?: NumberRange;
  calculatedCustomerPriceRange?: NumberRange;
  calculatedMinimumCustomerPriceRange?: NumberRange;
  partnerMinAbsoluteMarginRange?: NumberRange;
  partnerMinRelativeMarginRange?: NumberRange;
  partnerRegularAbsoluteMarginRange?: NumberRange;
  partnerRegularRelativeMarginRange?: NumberRange;
  basePriceInMarketplaceCurrencyRange?: NumberRange;
  scCategoryRelativeCommissionRange?: NumberRange;
  mpRelativeCommissionWithVatRange?: NumberRange;
  mpRelativeCommissionWithoutVatRange?: NumberRange;
  competitionCheaperOnMarketplace?: boolean;
  marketCompetitionCheaper?: boolean;
  priceDecreasePossibilities?: PriceDecreasePossibility[];
  partnerProductCodes?: string[];
  ean?: string;
  eshopProductCode?: string;
  eshopProductStatuses?: ProductStates[];
  handlingTimeRange?: NumberRange;
  onHold?: boolean;
  stockRange?: NumberRange;
  priceRange?: NumberRange;
  recommendedRetailPriceRange?: NumberRange;
  forcedPriceRange?: NumberRange;
  brand?: string;
  supplier?: string;
  customField?: string;
  categoryId?: string;
  pricingRequestStatuses?: PricingRequestStatus[];
  alertPriorities?: PricingAlertPriority[];
  maxPricingRequestId?: number;
};

export interface PricingDataSearchResult {
  page: PagedResponse<PricingDataListItem>;
  maxPricingRequestId?: number;
}

export interface PricingAvailabilityUpdateResult {
  total: number;
  updated: number;
  failed: number;
  report?: string;
}

export interface PricingImportResult {
  total: number;
  updated: number;
  failed: number;
  report?: string;
}

export type ProductListItem = {
  id: number;
  type: ProductTypes;
  partnerId: number;
  partnerCode: string;
  partnerName: string;
  eshopId: number;
  eshopCode: string;
  eshopName: string;
  partnerProductCode: string;
  ean: string;
  eshopProductCode: string;
  title: string;
  category: string;
  brand: string;
  price: number;
  forcedPrice: number;
  useForcedPrice: boolean;
  currency: string;
  status: ProductStates;
  deliveryDelay: number;
  freeDelivery: boolean;
  parentId: number;
  inStock: number;
  weight: number;
  weightUom: string;
  errorMessage: string;
  errorUpdateMessage: string;
  asyncOperationId: string | null;
  branchCode?: string;
  shippingGroup?: string;
  handlingTime?: number;
  minimumPrice?: number;
  automaticPricingRuleId?: string;
  temporaryHold: boolean;
  additionalPrices?: AdditionalPrice[];
  onHoldReason?: string;
  b2bPrice?: number;
  b2bPriceCurrency?: string;
  b2bPriceVat?: number;
  supplier?: string;
  isInPricing: boolean;
};

export interface AdditionalPrice {
  marketplaceCode: string;
  price?: number | null;
  minimumPrice?: number | null;
  forcedPrice?: number | null;
  vat?: number | null;
  currency?: string | null;
  pricingRuleId?: string | null;
  base: boolean;
}

export type ProductListFilter = {
  globalFilter?: string;
  eshopIds: number[];
  additionalMarketplaceCodes: string[];
  states: ProductStates[];
  categories: string[];
  brands: string[];
  suppliers: string[];
  productCode?: string;
  ean?: string;
  title?: string;
  priceRange?: NumberRange;
  stockRange?: NumberRange;
  weightRange?: NumberRange;
  onlyErrors?: boolean;
  handlingTimeRange?: NumberRange;
  onlyForcedPrice?: boolean;
  holdStatuses: HoldStatus[];
  isInPricing?: boolean;
};

export interface ProductImportResponse {
  productCount?: number;
  errorCount?: number;
  draftCount?: number;
  skippedCount?: number;
  updatedProducts?: number;
  createdProducts?: number;
  updatedVariants?: number;
  createdVariants?: number;
  errorCsv?: string;
  returnCsv?: string;
}

export interface AllegroUser {
  id: string;
  login: string;
  firstName: string;
  lastName: string;
  email: string;
  company: {
    name: string,
    tax: string
  };
  baseMarketplace: {
    id: string;
  }
}

export interface AllegroLinkingInfo {
  linked: boolean;
  linkUrl: string | null;
  allegroUser: AllegroUser | null;
  eshopCode: string;
  errorMessage?: string;
}

export interface SerialNumber {
  id: number;
  orderLineId: number;
  serialNumber?: string;
}

export interface OrderItem {
  id: number;
  partnerProductCode?: string;
  ean?: string;
  orderProductCode?: string;
  title?: string;
  serialNumbers: SerialNumber[];
}

export interface UpdateSerialNumbers {
  serialNumbers: SerialNumber[];
}

export interface OrderWithItems extends OrderListItem {
  items: OrderItem[];
}

export interface KauflandExternalShippingGroup {
  currency: string;
  shippingGroupId: number;
  storefront: string;
  name: string;
  type: string;
  isDefault: boolean;
  regions: KauflandExternalShippingGroupRegion[];
}

export interface KauflandExternalShippingGroupRegion {
  countries: string[];
  shippingOptions: KauflandExternalShippingGroupOption[];
}

export interface KauflandExternalShippingGroupOption {
  name: string;
  firstCost: number;
  nextCost: number;
  maxCost: number;
  freeCost: number;
  cutOffTime: string;
  minimumTransportTime: number;
  maximumTransportTime: number;
}

export interface KauflandTransportMapping {
  partnerCode: string;
  shippingGroupId: string;
  eshopCarrierId: number;
}

export enum MpiDeliveryType {
  PARCEL_SHOP = 'PARCEL_SHOP',
  PARCEL_BOX = 'PARCEL_BOX',
  HOME_DELIVERY = 'HOME_DELIVERY',
}

export const MpiDeliveryTypeLabels = {
  [MpiDeliveryType.PARCEL_SHOP]: 'Parcel shop',
  [MpiDeliveryType.PARCEL_BOX]: 'Parcel box',
  [MpiDeliveryType.HOME_DELIVERY]: 'Home delivery',
};

export interface EshopCarrier {
  id: number;
  eshopCode: string;
  name?: string;
  portalName?: string;
  eshopCarrierCode: string;
  eshopCarrierType?: string;
  carrierId: number;
  defaultMpiDeliveryType?: MpiDeliveryType;
  maxWeight?: number;
  managedByPartner?: boolean;
  maxParcelCount?: number;
  ignoreIban: boolean;
  splitShipmentByPackage: boolean;
  identifier: string;
  secondaryEshopCarrierCode?: string;
}

export interface KauflandCarrier {
  eshopCarrierId: number;
  carrierCode: string;
}

export interface KauflandExternalCarrier {
  name: string;
  isHauler: boolean;
}

export interface Carrier {
  id: number;
  identifier: string;
}

export interface PartnerEshopBankDetails {
  bankAccount?: string;
  bankAccountName?: string;
}

export interface PartnerEshopDefaults {
  defaultShippingRateId?: string;
  defaultReturnPolicyId?: string;
  defaultWarrantyId?: string;
}

export interface PartnerEshopResponsibilities {
  defaultResponsiblePersonId?: string;
  defaultResponsibleProducerId?: string;
}

export interface PartnerEshopCrossDock {
  enabled: boolean;
  code: string;
}

export interface PartnerEshopPricingEnablerSetup {
  enabled: boolean;
  marketplaceCode: string;
}

export interface PartnerEshopAutomaticLabelsPrintSetup {
  automaticLabelsPrintEnabled: boolean;
  automaticLabelsPrintOrderStatus?: OrderStates;
}

export interface SalesEntity {
  code: string;
  name: string;
}

export interface PartnerEshopSalesEntity {
  salesOrganizationCode: string;
  salesOrganizationName: string;
  partnerCode: string;
  partnerName: string;
  marketplaceCode: string;
  apiGroup: ApiGroup;
}

export interface OrderImportResult {
  totalCount: number;
  updatedCount: number;
  failedCount: number;
  warningCount: number;
  errorReport?: string;
  reimportReport?: string;
}

export interface CsvImportResponse {
  total: number;
  failed: number;
  updated?: number | null;
  report?: string;
}

export interface ProductsFilterData {
  brands: string[];
  categories: string[];
  statuses: ProductStates[];
  suppliers: string[];
}
